import type { ChangeEvent, FocusEvent } from 'react'

import { useState, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import { addParamToPath, dimmBackground } from 'utils'
import copy from 'copy.json'
import { basePaths, paths, clientPaths, adminPanelPaths, clientPanelPaths } from 'paths'

import useIsClientPage from 'hooks/useIsClientPage'

import Button from 'components/Button'
import Input from 'components/Input'
import InputLabel from 'components/InputLabel'
import AddResourceContent from 'components/AddResourceContent'
import { maxLength, validAlphanumericField } from 'utils/validators'

export default function AdditionalInformationScreen() {
  const { currentUser, clearFieldErrors } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isClientPage = useIsClientPage()

  const [error, setError] = useState<string | undefined>('')
  const [electricEnergyMeterNumber, setElectricEnergyMeterNumber] = useState('')

  const sellerId = location.state
  const urlBase = isClientPage
    ? basePaths.client_panel
    : `${basePaths.admin_panel}/${clientPaths.seller}`

  useEffect(dimmBackground)

  useEffect(() => clearFieldErrors, [clearFieldErrors])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setElectricEnergyMeterNumber(event.currentTarget.value)
  }

  const validateInput = (event: FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    const errorMessage = validAlphanumericField(value) || maxLength(value, 100)

    setError(errorMessage)
  }

  const handleGoNext = () => {
    if (error) return

    navigate(`${urlBase}/${paths.add_offer}`, {
      state: { electricEnergyMeterNumber, sellerId }
    })
  }

  const handleGoBack = () => {
    if (isClientPage) {
      navigate(clientPanelPaths.offer_data)
    } else {
      if (sellerId) {
        navigate(addParamToPath(adminPanelPaths.seller.search, sellerId))
      } else {
        navigate(adminPanelPaths.seller.search)
      }
    }
  }

  if (!currentUser && !isClientPage) return <Navigate to={basePaths.admin_panel} />

  return (
    <AddResourceContent>
      <h1>{copy.seller.additional_information.heading}</h1>
      <p>{copy.seller.additional_information.paragraph}</p>

      <ElectricEnergyMeterNumberWrapper>
        <InputLabel>
          <span>{copy.common.form.electric_energy_meter_number}</span>
          <span>{error}</span>
          <Input
            name="electric_energy_meter_number"
            value={electricEnergyMeterNumber}
            onChange={handleChange}
            onBlur={validateInput}
          />
        </InputLabel>

        <Link to={`${urlBase}/${paths.add_planned_offer}`} state={{ sellerId }}>
          {copy.seller.additional_information.link}
        </Link>
      </ElectricEnergyMeterNumberWrapper>

      <Button disabled={!electricEnergyMeterNumber.trim()} onClick={handleGoNext}>
        {copy.common.button.next}
      </Button>

      <Button secondary onClick={handleGoBack} type="button">
        {copy.common.button.go_back}
      </Button>
    </AddResourceContent>
  )
}

const ElectricEnergyMeterNumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > a {
    text-decoration: none;
    color: var(--green);
  }
`
